import { useRef, type CSSProperties } from "react";
import type { TLImageShape } from "@tldraw/tlschema";
import { guideArea, type GuideArea } from "./creative-tools";

/** A temporary local preview, never an annotation or a change to the shared source. */
export function CreativeAreaGuide({
  shape,
  src,
  area,
  onChange,
}: {
  shape: TLImageShape;
  src: string;
  area: GuideArea | null;
  onChange: (area: GuideArea | null) => void;
}) {
  const origin = useRef<{ x: number; y: number } | null>(null);
  const crop = shape.props.crop || {
    topLeft: { x: 0, y: 0 },
    bottomRight: { x: 1, y: 1 },
  };
  const cw = Math.max(0.001, crop.bottomRight.x - crop.topLeft.x),
    ch = Math.max(0.001, crop.bottomRight.y - crop.topLeft.y);
  const imageStyle: CSSProperties = {
    width: `${100 / cw}%`,
    height: `${100 / ch}%`,
    left: `${(-crop.topLeft.x * 100) / cw}%`,
    top: `${(-crop.topLeft.y * 100) / ch}%`,
  };
  return (
    <div className="creative-area-editor">
      <div
        className="creative-area-preview"
        style={{
          aspectRatio: `${shape.props.w} / ${shape.props.h}`,
          maxWidth: Math.min(380, (180 * shape.props.w) / shape.props.h),
        }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          const r = e.currentTarget.getBoundingClientRect();
          origin.current = {
            x: (e.clientX - r.left) / r.width,
            y: (e.clientY - r.top) / r.height,
          };
          e.currentTarget.setPointerCapture(e.pointerId);
          e.preventDefault();
        }}
        onPointerMove={(e) => {
          if (!origin.current) return;
          const r = e.currentTarget.getBoundingClientRect();
          onChange(
            guideArea(origin.current, {
              x: (e.clientX - r.left) / r.width,
              y: (e.clientY - r.top) / r.height,
            }),
          );
        }}
        onPointerUp={() => {
          origin.current = null;
        }}
        onPointerCancel={() => {
          origin.current = null;
        }}
      >
        <div
          className="creative-area-image"
          style={{
            transform: `scale(${shape.props.flipX ? -1 : 1},${shape.props.flipY ? -1 : 1})`,
          }}
        >
          <img
            src={src}
            alt="Selected image crop for area guidance"
            draggable={false}
            style={imageStyle}
          />
        </div>
        {area && (
          <span
            className="creative-area-rectangle"
            style={{
              left: `${area.x * 100}%`,
              top: `${area.y * 100}%`,
              width: `${area.w * 100}%`,
              height: `${area.h * 100}%`,
            }}
          />
        )}
      </div>
      <small>
        Drag or choose a region. AI may also change areas outside it.
      </small>
      <div className="creative-chips" aria-label="Area presets">
        <button
          type="button"
          onClick={() => onChange({ x: 0, y: 0, w: 0.5, h: 1 })}
        >
          Left half
        </button>
        <button
          type="button"
          onClick={() => onChange({ x: 0.5, y: 0, w: 0.5, h: 1 })}
        >
          Right half
        </button>
        <button
          type="button"
          onClick={() => onChange({ x: 0.25, y: 0.25, w: 0.5, h: 0.5 })}
        >
          Centre
        </button>
        <button type="button" onClick={() => onChange(null)}>
          Clear
        </button>
      </div>
      <span className="creative-sr-status" role="status">
        {area
          ? `Area selected: ${Math.round(area.w * 100)}% wide, ${Math.round(area.h * 100)}% high`
          : "No area selected"}
      </span>
    </div>
  );
}
